import { useState } from 'react';
import { Link } from 'react-router-dom';
import { App, Button, Popover, Select } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';
import { useCompanies } from '../../tasks/hooks/useTaskLookups.js';
import { useUpdateDealMutation } from '../hooks/useDealMutations.js';
import type { DealDetail } from '../api/deals.client.js';

export function DealCompanyField({ deal }: { deal: DealDetail }) {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const query = useCompanies();
  const update = useUpdateDealMutation();
  const [open, setOpen] = useState(false);
  const company = deal.company ?? deal.contact?.companyRef;
  const save = (companyId?: string, clearContact = false): void => {
    update.mutate(
      {
        id: deal.id,
        input: {
          companyId: companyId || null,
          ...(clearContact ? { contactId: null } : {}),
          ...(deal.updatedAt ? { expectedUpdatedAt: deal.updatedAt } : {}),
        },
      },
      {
        onSuccess: () => setOpen(false),
        onError: () => {
          void message.error(t('crm.entity_detail.save_error'));
        },
      },
    );
  };
  return (
    <div className="entity-company-field">
      {company ? <Link to={`/companies/${company.id}`}>{company.name}</Link> : '—'}
      <Popover
        open={open}
        onOpenChange={setOpen}
        trigger="click"
        title={t('crm.entity_detail.company_edit')}
        content={
          query.isError ? (
            <span role="alert">{t('crm.entity_detail.load_error')}</span>
          ) : (
            <Select
              aria-label={t('crm.entity_detail.company_edit')}
              style={{ width: 240 }}
              allowClear
              showSearch
              optionFilterProp="label"
              value={company?.id}
              disabled={update.isPending}
              loading={query.isLoading}
              options={(query.data ?? []).map((item) => ({ value: item.id, label: item.name }))}
              onChange={(companyId: string | undefined) => {
                if (companyId && deal.contactId && companyId !== deal.contact?.companyRef?.id) {
                  modal.confirm({
                    title: t('crm.deal_detail.company_mismatch'),
                    okText: t('crm.entity_detail.confirm'),
                    cancelText: t('crm.entity_detail.cancel'),
                    onOk: () => save(companyId, true),
                  });
                } else save(companyId);
              }}
            />
          )
        }
      >
        <Button
          type="text"
          size="small"
          icon={<EditOutlined />}
          className="entity-company-edit"
          aria-label={t('crm.entity_detail.company_edit')}
        />
      </Popover>
    </div>
  );
}
